import { AlertTriangle, AlertCircle, Info } from 'lucide-react';

export type Severity = 'critical' | 'serious' | 'moderate' | 'minor';

interface SeverityBadgeProps {
  severity: Severity;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const SEVERITY_CONFIG = {
  critical: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200',
    dot: 'bg-severity-critical',
    icon: AlertTriangle,
    label: 'Critical',
  },
  serious: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200',
    dot: 'bg-severity-serious',
    icon: AlertCircle,
    label: 'Serious',
  },
  moderate: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
    dot: 'bg-severity-moderate',
    icon: Info,
    label: 'Moderate',
  },
  minor: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-200',
    dot: 'bg-severity-minor',
    icon: Info,
    label: 'Minor',
  },
} as const;

const SIZE_CONFIG = {
  sm: {
    padding: 'px-2 py-0.5',
    text: 'text-caption',
    iconSize: 12,
  },
  md: {
    padding: 'px-3 py-1.5',
    text: 'text-body-sm',
    iconSize: 14,
  },
  lg: {
    padding: 'px-4 py-2',
    text: 'text-body',
    iconSize: 16,
  },
} as const;

export function SeverityBadge({ severity, showLabel = true, size = 'md' }: SeverityBadgeProps) {
  const config = SEVERITY_CONFIG[severity];
  const sizeConfig = SIZE_CONFIG[size];
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${sizeConfig.padding} rounded-full ${sizeConfig.text} font-medium ${config.bg} ${config.text} border ${config.border}`}
      role="status"
      aria-label={`${config.label} severity`}
    >
      <Icon size={sizeConfig.iconSize} aria-hidden="true" />
      {showLabel && config.label}
    </span>
  );
}

interface SeverityDotProps {
  severity: Severity;
  size?: 'sm' | 'md' | 'lg';
}

const DOT_SIZE_CONFIG = {
  sm: 'w-2 h-2',
  md: 'w-3 h-3',
  lg: 'w-4 h-4',
} as const;

export function SeverityDot({ severity, size = 'md' }: SeverityDotProps) {
  const config = SEVERITY_CONFIG[severity];
  const dotSize = DOT_SIZE_CONFIG[size];

  return (
    <span
      className={`${dotSize} rounded-full ${config.dot}`}
      role="presentation"
      aria-hidden="true"
    />
  );
}

export function getSeverityColor(severity: Severity): string {
  return SEVERITY_CONFIG[severity].text;
}

export function getSeverityBgColor(severity: Severity): string {
  return SEVERITY_CONFIG[severity].bg;
}

export function getSeverityLabel(severity: Severity): string {
  return SEVERITY_CONFIG[severity].label;
}

export { SEVERITY_CONFIG };
